import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PaletteMode } from '@mui/material';
import {
  ThemeProvider as MuiThemeProvider,
  type Theme,
} from '@mui/material/styles';
import { useBrand } from './BrandContext';
import { createBrandTheme } from '../theme/theme';

const STORAGE_KEY = 'mui-theme-mode';

export interface ThemeContextValue {
  theme: Theme;
  mode: PaletteMode;
  setMode: (mode: PaletteMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialMode(): PaletteMode {
  if (typeof window === 'undefined') {
    return 'light';
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // ignore
  }
  // TODO: Uncomment when we have a way to persist the theme to user preferences
  // return window.matchMedia('(prefers-color-scheme: dark)').matches
  //   ? 'dark'
  //   : 'light';
  return 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { colors } = useBrand();
  const [mode, setModeState] = useState<PaletteMode>(getInitialMode);

  const setMode = useCallback((next: PaletteMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const theme = useMemo(() => createBrandTheme(colors, mode), [colors, mode]);

  const value = useMemo(
    () => ({ theme, mode, setMode, toggleTheme }),
    [theme, mode, setMode, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
